import ReactGA from "react-ga4"
import { CometChat } from '@cometchat/chat-sdk-javascript';
import {
  getStorageData,
  setStorageData,
  removeStorageData,
} from '../Utilities';
import { AUTH_CONFIG } from '../auth-config';
const config = require('../config');

class TokenManager {
  private static instance: TokenManager;
  private isRefreshing = false;
  private refreshQueue: Array<(token: string) => void> = [];

  private constructor() {}

  static getInstance(): TokenManager {
    if (!TokenManager.instance) {
      TokenManager.instance = new TokenManager();
    }
    return TokenManager.instance;
  }

  isTokenError(error: any): boolean {
    if (!error) return false;

    return (
      Array.isArray(error.errors) &&
      error.errors.length &&
      error.errors[0]?.token &&
      AUTH_CONFIG.tokenErrorMessages.includes(error.errors[0].token)
    );
  }

  enqueueRequest(callback: (token: string) => void) {
    this.refreshQueue.push(callback);
  }

  processQueue(newToken: string | null) {
    while (this.refreshQueue.length > 0) {
      const callback = this.refreshQueue.shift();
      if (callback && newToken) {
        callback(newToken);
      }
    }
  }

  clearQueue() {
    this.refreshQueue = [];
  }

  async handleLogout() {
    try {
      console.warn('[TokenManager] Logging out...');
      const isUser = await CometChat.getLoggedinUser();
      if (isUser) {
        await CometChat.logout();
      }

      ReactGA.set({ user_id: null })
      await Promise.all([
        removeStorageData(AUTH_CONFIG.profileKey),
        removeStorageData(AUTH_CONFIG.cometAuthTokenKey),
        removeStorageData(AUTH_CONFIG.accessTokenKey),
        removeStorageData(AUTH_CONFIG.refreshTokenKey),
      ]);

      if(window.location.pathname !== '/EmailAccountLoginBlock') {
        window.location.href = AUTH_CONFIG.loginRouteWithReason;
      }
    } catch (err) {
      console.error('[TokenManager] Logout failed:', err);
    }
  }

  async getNewTokenPair(): Promise<string | null> {
    try {
      const refreshToken = await getStorageData(AUTH_CONFIG.refreshTokenKey);
      if (!refreshToken) {
        await this.handleLogout();
        return null;
      }

      const refreshTokenEndpoint = `${config.baseURL}/${
        AUTH_CONFIG.refreshTokenEndpoint
      }?refresh_token=${encodeURIComponent(refreshToken)}`;

      const response = await fetch(refreshTokenEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      let data;
      try {
        data = await response.json();
      } catch (jsonError) {
        console.error('Failed to parse refresh token response:', jsonError);
        await this.handleLogout();
        return null;
      }

      if (response.ok && data.meta.token) {
        await setStorageData(AUTH_CONFIG.accessTokenKey, data.meta.token);
        return data.meta.token;
      }

      await this.handleLogout();
      return null;
    } catch (error) {
      console.error('Token refresh failed:', error);
      await this.handleLogout();
      return null;
    } finally {
      if (this.isRefreshing) {
        this.isRefreshing = false;
      }
    }
  }

  async handleTokenRefresh(): Promise<string | null> {
    if (this.isRefreshing) {
      return new Promise((resolve) => {
        this.enqueueRequest((token) => {
          if (token) resolve(token);
          else resolve(null);
        });
      });
    }

    this.isRefreshing = true;

    const newToken = await this.getNewTokenPair();

    if (!newToken) {
      this.clearQueue();
      return null;
    }

    this.processQueue(newToken);
    return newToken;
  }
}

export default TokenManager;
